import React, { useState } from 'react';
import {
  Search,
  User,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  History,
} from 'lucide-react';
import { useClassStudents } from '../../hooks/useProfessor';
import type { ClassStudentInfo } from '@/types/professor.types';

interface StudentsTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted = the live one
  academicYearId?: string;
}

const StudentsTab: React.FC<StudentsTabProps> = ({ groupeId, moduleElementId, academicYearId }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: students = [], isLoading, error } = useClassStudents(groupeId, moduleElementId, academicYearId);

  // Filter students by search query
  const filteredStudents = students.filter((student: ClassStudentInfo) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      student.firstName.toLowerCase().includes(search) ||
      student.lastName.toLowerCase().includes(search) ||
      student.studentCode.toLowerCase().includes(search) ||
      (student.email && student.email.toLowerCase().includes(search))
    );
  });

  // Get attendance rate color
  const getAttendanceColor = (rate: number): string => {
    if (rate >= 80) return 'text-green-600 bg-green-50';
    if (rate >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Erreur lors du chargement des étudiants</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un étudiant..."
          className="w-full max-w-md pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span>{filteredStudents.length} étudiant{filteredStudents.length > 1 ? 's' : ''}</span>
      </div>

      {/* Students list */}
      {filteredStudents.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun étudiant trouvé</h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchQuery ? 'Essayez de modifier votre recherche' : 'Aucun étudiant dans ce groupe'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Étudiant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Présences
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Absences
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Retards
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taux
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student: ClassStudentInfo) => (
                <tr key={student.id} className={student.isFormerMember ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {student.photoUrl ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={student.photoUrl}
                            alt={`${student.firstName} ${student.lastName}`}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span>
                            {student.lastName} {student.firstName}
                          </span>
                          {student.isFormerMember && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[11px] font-medium rounded-full"
                              title="Cet étudiant a quitté le groupe ; ses présences enregistrées ici sont conservées"
                            >
                              <History className="h-3 w-3" />
                              Ancien membre
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{student.studentCode}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {student.email && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail className="h-3.5 w-3.5" />
                          <span className="truncate max-w-[200px]">{student.email}</span>
                        </div>
                      )}
                      {student.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{student.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      {student.totalPresences}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-red-600">
                      <XCircle className="h-4 w-4" />
                      {student.totalAbsences}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-amber-600">
                      <Clock className="h-4 w-4" />
                      {student.totalLates}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 text-sm font-medium rounded-full ${getAttendanceColor(student.attendanceRate)}`}>
                      {student.attendanceRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudentsTab;
